
import React from "react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface AvatarUploadProps {
  uid: string;
  url: string | null;
  onUpload: (url: string) => void;
  size?: "sm" | "lg";
}

export function AvatarUpload({ uid, url, onUpload, size = "lg" }: AvatarUploadProps) {
  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("Je moet een afbeelding selecteren om te uploaden.");
      }

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      
      // Controleer bestandstype
      if (!fileExt || !['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt)) {
        throw new Error("Alleen jpg, png, gif en webp bestanden zijn toegestaan.");
      }

      // Controleer bestandsgrootte (50MB)
      if (file.size > 52428800) {
        throw new Error("Bestand is te groot. Maximum grootte is 50MB.");
      }

      const filePath = `${uid}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError, data } = await supabase.storage
        .from("profile-photos")
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      if (data) {
        const { data: { publicUrl } } = supabase.storage
          .from("profile-photos")
          .getPublicUrl(filePath);

        onUpload(publicUrl);
      }
      
      toast.success("Profielfoto succesvol geüpload");
    } catch (error: any) {
      toast.error(error.message || "Er is een fout opgetreden bij het uploaden van je profielfoto");
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <Avatar className={size === "lg" ? "h-24 w-24" : "h-10 w-10"}>
        <AvatarImage src={url || undefined} />
        <AvatarFallback>...</AvatarFallback>
      </Avatar>
      <div className="flex justify-center">
        <Button
          variant="outline"
          size="sm"
          className="relative"
          type="button"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload foto
          <input
            type="file"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            accept="image/*"
            onChange={uploadAvatar}
          />
        </Button>
      </div>
    </div>
  );
}
